import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import { formatDate as canonicalFormatDate } from "@/libs/i18n/format-date";
import {
  WithdrawalPolicyData,
  WithdrawalPolicyResponse,
} from "@/types/withdrawal";

export const formatPolicyType = (policyType: string): string => {
  switch (policyType) {
    case ANNUAL_MULTI_TRIP:
      return "Annual Multi Trip";
    case SINGLE_TRIP:
      return "Single Trip";
    default:
      return policyType;
  }
};

export const formatPolicyPackage = (policyPackage: string): string => {
  return (
    policyPackage.charAt(0).toUpperCase() + policyPackage.slice(1).toLowerCase()
  );
};

export const formatDestination = (
  policyType: string,
  area: string,
  toLocationCountryDetails?: {
    attributes?: {
      countryCode?: string;
      countryName?: string;
    };
  }[]
): string => {
  // For single trip
  if (policyType === SINGLE_TRIP && toLocationCountryDetails?.length) {
    return toLocationCountryDetails
      .map((location) => location?.attributes?.countryName)
      .filter(Boolean)
      .join(", ");
  }

  // For annual multi-trip
  const areaMapping: Record<string, string> = {
    EUROPE_LR:
      "Europe Excluding: Cyprus, Egypt, Greece (including the Greek Islands), Malta, Morocco, Spain (including the Balearic Islands and the Canary Islands), Turkey and Tunisia",
    EUROPE_HR:
      "Europe Including: Cyprus, Egypt, Greece (including the Greek Islands), Malta, Morocco, Spain (including the Balearic Islands and the Canary Islands), Turkey and Tunisia",
    WORLD_LR:
      "Worldwide Excluding: USA, Canada, Caribbean, Bermuda, Mexico, Thailand, China and Hong Kong",
    WORLD_HR: "Worldwide",
  };

  return areaMapping[area] || area;
};

export const formatCoverDates = (
  startDate: string,
  endDate: string,
  policyType: string
): string => {
  const formatDate = (dateString: string): string =>
    canonicalFormatDate(dateString, "dd/MM/yy");

  if (policyType === ANNUAL_MULTI_TRIP) {
    return `1 Year (Starting from ${formatDate(startDate)})`;
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const formatPaidOnDate = (creationDate: string): string => {
  const date = new Date(creationDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

/**
 * Formats a date from YYYY-MM-DD format to DD/MM/YYYY format
 * @param dateString - Date string in YYYY-MM-DD format (e.g., "2026-08-12")
 * @returns Formatted date string in DD/MM/YYYY format (e.g., "12/08/2026")
 */
export const formatWithdrawalDate = (dateString: string | null): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}/${month}/${year}`;
};

export const transformWithdrawalData = (
  apiResponse: WithdrawalPolicyResponse
): WithdrawalPolicyData => {
  const { attributes } = apiResponse.data;
  const {
    organiser,
    tripDetails,
    travellerDetails,
    paymentDetails,
    policyNumber,
  } = attributes;

  return {
    policy: {
      organiser: `${organiser.firstName} ${organiser.lastName}`,
      policyNumber,
      coverType: formatPolicyType(tripDetails.policyType),
      coverLevel: formatPolicyPackage(tripDetails.policyPackage),
      destination: formatDestination(
        tripDetails.policyType,
        tripDetails.area,
        tripDetails.toLocationCountryDetails
      ),
      coverDates: formatCoverDates(
        tripDetails.startDate,
        tripDetails.endDate,
        tripDetails.policyType
      ),
      travellers: String(travellerDetails.traveller.length),
    },
    financial: {
      originalPremium:
        paymentDetails.paymentOptions[0]?.cardPayment?.card?.price || "0.00",
      paidOn: formatPaidOnDate(tripDetails.policyCreationDate),
      cardLastFourDigits: paymentDetails.cardSummary,
    },
    referenceId: apiResponse.data.referenceId,
    version: apiResponse.data.version,
    withdrawalData: {
      withdrawalEligibilityStatus:
        paymentDetails.withdrawalData.withdrawalEligibilityStatus,
      modifiedDate: paymentDetails.withdrawalData.modifiedDate
        ? formatWithdrawalDate(paymentDetails.withdrawalData.modifiedDate)
        : null,
    },
  };
};
